import { Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  useTheme,
  Stack,
  useMediaQuery
} from '@mui/material';
import { motion } from 'framer-motion';
import KitchenIcon from '@mui/icons-material/Kitchen';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import Masterchef from "../assets/Masterchef.png";

const PageNotFound = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Container maxWidth="md" sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      py: 8
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Stack spacing={4} alignItems="center">
          {/* 404 Number with Icon */}
          <Box sx={{ position: 'relative' }}>
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: isMobile ? '6rem' : '8rem',
                fontWeight: 900,
                color: theme.palette.primary.main,
                lineHeight: 1
              }}
            >
              4
              <motion.span
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <KitchenIcon 
                  sx={{ 
                    fontSize: isMobile ? '5rem' : '6rem',
                    verticalAlign: 'middle',
                    color: theme.palette.error.main,
                    mx: 1
                  }} 
                />
              </motion.span>
              4
            </Typography>
          </Box>

          {/* Masterchef Image */}
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box
              component="img"
              src={Masterchef}
              alt="Master Chef"
              sx={{
                width: isMobile ? '200px' : '300px',
                height: 'auto',
                borderRadius: '50%',
                border: `4px solid ${theme.palette.primary.light}`,
                boxShadow: theme.shadows[8],
              }}
            />
          </motion.div>

          {/* Text Content */}
          <Box>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                mb: 2,
                color: theme.palette.text.primary
              }}
            >
              Recipe Not Found!
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                maxWidth: '600px',
                color: theme.palette.text.secondary,
                fontSize: '1.1rem'
              }}
            >
              Our master chef couldn't find this page in the cookbook. 
              Maybe the ingredients got mixed up or the recipe was never written.
            </Typography>
          </Box>

          {/* Action Button */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              component={Link}
              to="/"
              variant="contained"
              color="primary"
              size="large"
              startIcon={<RestaurantIcon />}
              sx={{
                borderRadius: '50px',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                fontSize: '1rem',
                boxShadow: theme.shadows[4],
                '&:hover': {
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              Back to Kitchen
            </Button>
          </motion.div>

          {/* Footer Note */}
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ repeat: Infinity, duration: 3 }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                mt: 4,
                color: theme.palette.text.disabled,
                fontStyle: 'italic'
              }}
            >
              "Even the best chefs get lost sometimes!"
            </Typography>
          </motion.div>
        </Stack>
      </motion.div>
    </Container>
  );
};

export default PageNotFound;